import { AdminApi, AdminProspectFilterQuery } from "../api/admin-api";
import { ModalComponent } from "../components/modal";
import { ToastComponent } from "../components/toast";
import { SiretInputComponent } from "../components/siret-input";
import { evaluatePasswordRules } from "../utils/password-validator";
import { AdminTab, AdminUserItem, UserProfile } from "../types";
import {
  formatSiretDisplay,
  formatDate,
  formatDateTime,
  formatRelationType,
  escapeHtml,
} from "../utils/formatters";

export class AdminView {
  private currentUser: UserProfile | null = null;
  private loadedAccounts: AdminUserItem[] = [];

  private readonly currentQuery: AdminProspectFilterQuery = {
    sortBy: "date",
    order: "desc",
    filterApporteur: "all",
    filterSize: "all",
    search: "",
  };

  constructor() {
    this.initEventListeners();
  }

  public activate(user?: UserProfile): void {
    if (user) this.currentUser = user;
    this.loadStats();
    this.loadApporteursDropdown();
    this.loadProspects();
    this.loadSharedSirets();
    this.loadAccounts();
    this.switchTab("prospects");
  }

  private initEventListeners(): void {
    // Admin tab switches
    document
      .getElementById("adminTabProspectsBtn")
      ?.addEventListener("click", () => this.switchTab("prospects"));
    document
      .getElementById("adminTabSharedBtn")
      ?.addEventListener("click", () => this.switchTab("shared"));
    document
      .getElementById("adminTabApporteursBtn")
      ?.addEventListener("click", () => this.switchTab("apporteurs"));

    // Filter controls for prospects
    const sortSelect = document.getElementById(
      "adminSortBy",
    ) as HTMLSelectElement | null;
    const orderSelect = document.getElementById(
      "adminSortOrder",
    ) as HTMLSelectElement | null;
    const apporteurSelect = document.getElementById(
      "adminFilterApporteur",
    ) as HTMLSelectElement | null;
    const sizeSelect = document.getElementById(
      "adminFilterSize",
    ) as HTMLSelectElement | null;
    const searchInput = document.getElementById(
      "adminSearchInput",
    ) as HTMLInputElement | null;

    const triggerFilter = () => {
      this.currentQuery.sortBy = (sortSelect?.value || "date") as any;
      this.currentQuery.order = (orderSelect?.value || "desc") as any;
      this.currentQuery.filterApporteur = apporteurSelect?.value || "all";
      this.currentQuery.filterSize = sizeSelect?.value || "all";
      this.currentQuery.search = searchInput?.value || "";
      this.loadProspects();
    };

    sortSelect?.addEventListener("change", triggerFilter);
    orderSelect?.addEventListener("change", triggerFilter);
    apporteurSelect?.addEventListener("change", triggerFilter);
    sizeSelect?.addEventListener("change", triggerFilter);
    searchInput?.addEventListener("input", triggerFilter);

    // Filter controls for user accounts
    const userRoleFilter = document.getElementById(
      "adminUserFilterRole",
    ) as HTMLSelectElement | null;
    const userStatusFilter = document.getElementById(
      "adminUserFilterStatus",
    ) as HTMLSelectElement | null;
    const userSearchInput = document.getElementById(
      "adminUserSearchInput",
    ) as HTMLInputElement | null;

    userRoleFilter?.addEventListener("change", () => this.loadAccounts());
    userStatusFilter?.addEventListener("change", () => this.loadAccounts());
    userSearchInput?.addEventListener("input", () => this.loadAccounts());

    // Account modal interactions
    document
      .getElementById("btnOpenCreateUserModal")
      ?.addEventListener("click", () => {
        ModalComponent.openUserAccountModal();
      });

    document
      .getElementById("closeUserAccountModalBtn")
      ?.addEventListener("click", () => {
        ModalComponent.closeUserAccountModal();
      });
    document
      .getElementById("btnCancelUserAccountModal")
      ?.addEventListener("click", () => {
        ModalComponent.closeUserAccountModal();
      });

    // Reset password modal close buttons
    document
      .getElementById("closeUserPasswordModalBtn")
      ?.addEventListener("click", () => {
        ModalComponent.closeResetPasswordModal();
      });
    document
      .getElementById("btnCancelUserPasswordModal")
      ?.addEventListener("click", () => {
        ModalComponent.closeResetPasswordModal();
      });

    // Delete modal close buttons
    document
      .getElementById("closeUserDeleteModalBtn")
      ?.addEventListener("click", () => {
        ModalComponent.closeDeleteUserModal();
      });
    document
      .getElementById("btnCancelUserDeleteModal")
      ?.addEventListener("click", () => {
        ModalComponent.closeDeleteUserModal();
      });

    // SIRET input formatter on user account modal
    const modalUserSiret = document.getElementById(
      "modalUserSiret",
    ) as HTMLInputElement | null;
    if (modalUserSiret) {
      SiretInputComponent.attach(modalUserSiret, "modalUserSiretCounter");
    }

    // Live password validators on modals
    const modalPwd = document.getElementById(
      "modalUserPassword",
    ) as HTMLInputElement | null;
    modalPwd?.addEventListener("input", () => {
      this.updatePasswordCriteriaUI(
        modalPwd.value,
        "modalRuleLength",
        "modalRuleUpper",
        "modalRuleDigit",
        "modalRuleSpecial",
        "modalStrengthTag",
      );
    });

    const resetPwd = document.getElementById(
      "resetNewPassword",
    ) as HTMLInputElement | null;
    resetPwd?.addEventListener("input", () => {
      this.updatePasswordCriteriaUI(
        resetPwd.value,
        "resetRuleLength",
        "resetRuleUpper",
        "resetRuleDigit",
        "resetRuleSpecial",
        "resetStrengthTag",
      );
    });

    // Submit user account form (create or edit)
    const userAccountForm = document.getElementById(
      "userAccountForm",
    ) as HTMLFormElement | null;
    userAccountForm?.addEventListener("submit", (e) =>
      this.handleSaveUserAccount(e),
    );

    // Submit reset password form
    const userPasswordForm = document.getElementById(
      "userPasswordForm",
    ) as HTMLFormElement | null;
    userPasswordForm?.addEventListener("submit", (e) =>
      this.handleConfirmResetPassword(e),
    );

    // Confirm delete user button
    document
      .getElementById("btnConfirmDeleteUser")
      ?.addEventListener("click", () => this.handleConfirmDeleteUser());
  }

  private updatePasswordCriteriaUI(
    password: string,
    ruleLengthId: string,
    ruleUpperId: string,
    ruleDigitId: string,
    ruleSpecialId: string,
    tagId: string,
  ): void {
    const rules = evaluatePasswordRules(password);
    this.setRuleValid(ruleLengthId, rules.minLength);
    this.setRuleValid(ruleUpperId, rules.hasUpper);
    this.setRuleValid(ruleDigitId, rules.hasDigit);
    this.setRuleValid(ruleSpecialId, rules.hasSpecial);

    const tag = document.getElementById(tagId);
    if (!tag) return;

    if (rules.score === 0) {
      tag.textContent = "En attente";
      tag.style.color = "var(--text-dim)";
    } else if (rules.score <= 2) {
      tag.textContent = "Insuffisant";
      tag.style.color = "#f87171";
    } else if (rules.score === 3) {
      tag.textContent = "Moyen";
      tag.style.color = "#fbbf24";
    } else {
      tag.textContent = "Sécurisé ✓";
      tag.style.color = "#34d399";
    }
  }

  private setRuleValid(elementId: string, isValid: boolean): void {
    const el = document.getElementById(elementId);
    if (!el) return;
    const icon = el.querySelector(".rule-icon");
    if (isValid) {
      el.classList.remove("invalid");
      el.classList.add("valid");
      if (icon) icon.textContent = "✓";
    } else {
      el.classList.remove("valid");
      el.classList.add("invalid");
      if (icon) icon.textContent = "✕";
    }
  }

  public switchTab(tab: AdminTab): void {
    const tabs: readonly AdminTab[] = [
      "prospects",
      "shared",
      "apporteurs",
    ] as const;
    tabs.forEach((t) => {
      const btnId = `adminTab${t.charAt(0).toUpperCase() + t.slice(1)}Btn`;
      const panelId = `admin${t.charAt(0).toUpperCase() + t.slice(1)}Tab`;

      const btn = document.getElementById(btnId);
      const panel = document.getElementById(panelId);

      if (btn && panel) {
        if (t === tab) {
          btn.classList.add("active");
          panel.style.display = "block";
        } else {
          btn.classList.remove("active");
          panel.style.display = "none";
        }
      }
    });

    if (tab === "prospects") this.loadProspects();
    if (tab === "shared") this.loadSharedSirets();
    if (tab === "apporteurs") this.loadAccounts();
  }

  public async loadStats(): Promise<void> {
    try {
      const stats = await AdminApi.getStats();
      const kpiApporteurs = document.getElementById("kpiApporteurs");
      const kpiProspects = document.getElementById("kpiProspects");
      const kpiShared = document.getElementById("kpiSharedSirets");

      if (kpiApporteurs)
        kpiApporteurs.textContent = String(stats.totalApporteurs);
      if (kpiProspects) kpiProspects.textContent = String(stats.totalProspects);
      if (kpiShared) kpiShared.textContent = String(stats.sharedSirets);

      // Badge pour les inscriptions en attente de validation
      const pendingBadge = document.getElementById("adminPendingBadge");
      if (pendingBadge) {
        if (stats.pendingApporteurs && stats.pendingApporteurs > 0) {
          pendingBadge.textContent = `${stats.pendingApporteurs} en attente`;
          pendingBadge.style.display = "inline-flex";
        } else {
          pendingBadge.style.display = "none";
        }
      }
    } catch (err: any) {
      console.error("Failed to load admin stats:", err);
    }
  }

  private async loadApporteursDropdown(): Promise<void> {
    try {
      const apporteurs = await AdminApi.getApporteurs();
      const select = document.getElementById(
        "adminFilterApporteur",
      ) as HTMLSelectElement | null;
      if (!select) return;

      select.innerHTML =
        '<option value="all">Tous les apporteurs</option>' +
        apporteurs
          .map(
            (a) =>
              `<option value="${a.id}">${escapeHtml(a.raison_sociale)}</option>`,
          )
          .join("");
    } catch (err: any) {
      console.error("Failed to load apporteurs dropdown:", err);
    }
  }

  /**
   * Loads prospects with sorting (by date, apporteur, size) and filtering
   */
  public async loadProspects(): Promise<void> {
    try {
      const prospects = await AdminApi.getProspects(this.currentQuery);
      const tabCount = document.getElementById("adminProspectsTabCount");
      if (tabCount) tabCount.textContent = String(prospects.length);

      const tbody = document.getElementById("adminProspectsTableBody");
      if (!tbody) return;

      if (prospects.length === 0) {
        tbody.innerHTML = `
          <tr>
            <td colspan="8" style="text-align:center; padding: 2.5rem; color: var(--text-dim);">
              Aucun prospect ne correspond aux critères de filtre sélectionnés.
            </td>
          </tr>
        `;
        return;
      }

      tbody.innerHTML = prospects
        .map(
          (p) => `
        <tr>
          <td><span class="siret-code">${formatSiretDisplay(p.prospect_siret)}</span></td>
          <td><strong>${formatDate(p.registration_date)}</strong></td>
          <td>
            <div style="font-weight:600; color:#fff;">${escapeHtml(p.apporteur_name)}</div>
            <div style="font-size:0.75rem; color:var(--text-dim);">SIRET : ${formatSiretDisplay(p.apporteur_siret)}</div>
            <div style="font-size:0.75rem; color:var(--text-dim);">${escapeHtml(p.apporteur_email)} | ${escapeHtml(p.apporteur_telephone)}</div>
          </td>
          <td>${escapeHtml(p.contact_position)}</td>
          <td>${escapeHtml(p.company_size)}</td>
          <td><span class="relation-badge relation-${p.relation_type}">${formatRelationType(p.relation_type)}</span></td>
          <td>
            <div class="relation-details-box" style="max-width: 250px;">
              ${escapeHtml(p.relation_details || "Aucune précision")}
            </div>
          </td>
          <td>
            ${
              p.is_shared
                ? `<button class="badge-tag badge-shared" data-jump-siret="${p.prospect_siret}" title="Cliquer pour afficher la comparaison groupée">
                   ⚡ Partagé (${p.total_apporteurs_for_siret})
                 </button>`
                : `<span class="badge-tag badge-unique">✓ Unique</span>`
            }
          </td>
        </tr>
      `,
        )
        .join("");

      tbody
        .querySelectorAll<HTMLButtonElement>("button[data-jump-siret]")
        .forEach((btn) => {
          btn.addEventListener("click", () => {
            const siret = btn.dataset.jumpSiret;
            if (siret) this.jumpToSharedSiret(siret);
          });
        });
    } catch (err: any) {
      console.error("Failed to load admin prospects:", err);
    }
  }

  private jumpToSharedSiret(siret: string): void {
    this.switchTab("shared");
    setTimeout(() => {
      const card = document.getElementById(`shared-card-${siret}`);
      if (card) {
        card.scrollIntoView({ behavior: "smooth", block: "center" });
        card.style.boxShadow = "0 0 25px rgba(245, 158, 11, 0.6)";
        setTimeout(() => {
          card.style.boxShadow = "";
        }, 2500);
      }
    }, 150);
  }

  public async loadSharedSirets(): Promise<void> {
    try {
      const groups = await AdminApi.getSharedSirets();
      const tabCount = document.getElementById("adminSharedTabCount");
      if (tabCount) tabCount.textContent = String(groups.length);

      const container = document.getElementById("sharedSiretsContainer");
      if (!container) return;

      if (groups.length === 0) {
        container.innerHTML = `
          <div style="text-align:center; padding:3rem; color:var(--text-dim);">
            <div style="font-size:2.5rem; margin-bottom:0.75rem;">🎉</div>
            <h3>Aucun conflit de SIRET détecté</h3>
          </div>
        `;
        return;
      }

      container.innerHTML = groups
        .map(
          (group) => `
        <div class="shared-group-card" id="shared-card-${group.prospect_siret}">
          <div class="shared-group-header">
            <div class="shared-siret-title">
              <span class="siret-code" style="font-size:1.1rem; padding: 0.35rem 0.8rem;">
                SIRET Prospect : ${formatSiretDisplay(group.prospect_siret)}
              </span>
              <span class="shared-conflict-badge">
                ⚡ ${group.total_apporteurs} apporteurs en concurrence
              </span>
            </div>
            <div class="shared-meta-dates">
              Premier enregistrement le <strong>${formatDate(group.first_registration_date)}</strong>
            </div>
          </div>

          <div class="shared-submissions-grid">
            ${group.submissions
              .map(
                (sub) => `
              <div class="submission-detail-card ${sub.is_first_registrant ? "first-registrant" : ""}">
                <div class="submission-rank-badge ${sub.is_first_registrant ? "rank-first" : "rank-second"}">
                  ${sub.is_first_registrant ? "👑 1er Déclarant (Priorité)" : `#${sub.rank_order} Déclarant Ultérieur`}
                </div>

                <div class="submission-apporteur-name">
                  ${escapeHtml(sub.apporteur_name)}
                </div>

                <div class="submission-apporteur-meta">
                  <span>🏢 SIRET Apporteur : <strong>${formatSiretDisplay(sub.apporteur_siret)}</strong></span>
                  <span>✉️ ${escapeHtml(sub.apporteur_email)}</span>
                  <span>📞 ${escapeHtml(sub.apporteur_telephone)}</span>
                </div>

                <hr style="border: 0; border-top: 1px solid var(--border-subtle); margin: 0.2rem 0;">

                <div class="submission-row">
                  <span class="submission-label">Date d'enregistrement déclarée</span>
                  <span class="submission-value">📅 <strong>${formatDate(sub.registration_date)}</strong> (Créé le ${formatDateTime(sub.created_at)})</span>
                </div>

                <div class="submission-row">
                  <span class="submission-label">Poste du contact</span>
                  <span class="submission-value">👤 <strong>${escapeHtml(sub.contact_position)}</strong></span>
                </div>

                <div class="submission-row">
                  <span class="submission-label">Taille de l'entreprise</span>
                  <span class="submission-value">👥 ${escapeHtml(sub.company_size)}</span>
                </div>

                <div class="submission-row">
                  <span class="submission-label">Type de relation</span>
                  <span class="submission-value">
                    <span class="relation-badge relation-${sub.relation_type}">${formatRelationType(sub.relation_type)}</span>
                  </span>
                </div>

                <div class="submission-row">
                  <span class="submission-label">Précision sur le lien saisie par cet apporteur</span>
                  <div class="relation-details-box">
                    "${escapeHtml(sub.relation_details || "Aucune précision renseignée")}"
                  </div>
                </div>
              </div>
            `,
              )
              .join("")}
          </div>
        </div>
      `,
        )
        .join("");
    } catch (err: any) {
      console.error("Failed to load shared SIRETs:", err);
    }
  }

  /**
   * Complete account administration loader
   */
  public async loadAccounts(): Promise<void> {
    try {
      const roleFilter =
        (document.getElementById("adminUserFilterRole") as HTMLSelectElement)
          ?.value || "all";
      const statusFilter =
        (document.getElementById("adminUserFilterStatus") as HTMLSelectElement)
          ?.value || "all";
      const searchVal =
        (document.getElementById("adminUserSearchInput") as HTMLInputElement)
          ?.value || "";

      const users = await AdminApi.getUsers(roleFilter, searchVal, statusFilter);
      this.loadedAccounts = users;

      const tabCount = document.getElementById("adminUsersTabCount");
      if (tabCount) tabCount.textContent = String(users.length);

      const tbody = document.getElementById("adminApporteursTableBody");
      if (!tbody) return;

      if (users.length === 0) {
        tbody.innerHTML = `
          <tr>
            <td colspan="8" style="text-align:center; padding: 2.5rem; color: var(--text-dim);">
              Aucun compte utilisateur ne correspond à votre recherche.
            </td>
          </tr>
        `;
        return;
      }

      tbody.innerHTML = users
        .map((u) => {
          const isSelf = this.currentUser
            ? this.currentUser.id === u.id
            : false;
          const roleBadge =
            u.role === "admin"
              ? '<span class="badge-tag badge-role-admin">🛡️ Administrateur</span>'
              : '<span class="badge-tag badge-role-apporteur">💼 Apporteur</span>';

          let statusBadge = '';
          if (u.status === 'pending') {
            statusBadge = '<span class="badge-tag" style="background: rgba(245, 158, 11, 0.15); color: #fbbf24; border: 1px solid rgba(245, 158, 11, 0.35);">⏳ En attente</span>';
          } else if (u.status === 'rejected') {
            statusBadge = '<span class="badge-tag" style="background: rgba(239, 68, 68, 0.15); color: #f87171; border: 1px solid rgba(239, 68, 68, 0.35);">✕ Refusé</span>';
          } else {
            statusBadge = '<span class="badge-tag" style="background: rgba(16, 185, 129, 0.15); color: #34d399; border: 1px solid rgba(16, 185, 129, 0.35);">✓ Validé</span>';
          }

          return `
          <tr ${u.status === 'pending' ? 'style="background: rgba(245, 158, 11, 0.04);"' : ''}>
            <td>
              <div style="font-weight: 600; color: #fff;">${escapeHtml(u.raison_sociale)}</div>
              ${isSelf ? '<span style="font-size:0.72rem; color:#60a5fa; font-weight:600;">(Votre session actuelle)</span>' : ""}
            </td>
            <td>${roleBadge}</td>
            <td>${statusBadge}</td>
            <td><span class="siret-code">${formatSiretDisplay(u.siret)}</span></td>
            <td>
              <div style="font-size:0.85rem; color:var(--text-secondary);">${escapeHtml(u.email)}</div>
              <div style="font-size:0.75rem; color:var(--text-dim);">${escapeHtml(u.telephone)}</div>
            </td>
            <td>
              ${
                u.role === "admin"
                  ? '<span style="color:var(--text-dim); font-size:0.8rem;">Gestion globale</span>'
                  : `<div style="display:flex; align-items:center; gap:0.4rem;">
                     <span class="badge-count" style="background:#3b82f6;">${u.total_prospects}</span>
                     ${
                       u.shared_prospects_count > 0
                         ? `<span class="badge-tag badge-shared" style="font-size:0.68rem; padding:0.15rem 0.5rem;">⚠️ ${u.shared_prospects_count} partagé(s)</span>`
                         : '<span class="badge-tag badge-unique" style="font-size:0.68rem; padding:0.15rem 0.5rem;">0 conflit</span>'
                     }
                   </div>`
              }
            </td>
            <td>${formatDate(u.created_at)}</td>
            <td style="text-align: right;">
              <div class="table-actions-cell">
                ${
                  u.status === 'pending'
                    ? `<button type="button" class="btn-action-validate-pill" data-action="validate-user" data-user-id="${u.id}" title="Valider et approuver ce compte">
                        ✓ Valider
                       </button>
                       <button type="button" class="btn-action-icon danger" data-action="reject-user" data-user-id="${u.id}" title="Refuser cette inscription" aria-label="Refuser cette inscription">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                       </button>`
                    : u.status === 'rejected'
                    ? `<button type="button" class="btn-action-validate-pill" data-action="validate-user" data-user-id="${u.id}" title="Réactiver ce compte">
                        Réactiver
                       </button>`
                    : ''
                }
                <button type="button" class="btn-action-icon" data-action="edit-user" data-user-id="${u.id}" title="Modifier le compte" aria-label="Modifier le compte">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                </button>
                <button type="button" class="btn-action-icon" data-action="reset-pwd" data-user-id="${u.id}" title="Réinitialiser le mot de passe" aria-label="Réinitialiser le mot de passe">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 2l-2 2m-1.5 1.5L16 7l-2-2 1.5-1.5M19 4l-4 4M7 11a5 5 0 1 0 0 10 5 5 0 0 0 0-10zm0 7a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"/></svg>
                </button>
                <button type="button" class="btn-action-icon danger" data-action="delete-user" data-user-id="${u.id}" ${isSelf ? 'disabled title="Impossible de supprimer votre propre compte"' : 'title="Supprimer ce compte"'} aria-label="Supprimer ce compte">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                </button>
              </div>
            </td>
          </tr>
        `;
        })
        .join("");

      // Attach row action listeners
      tbody
        .querySelectorAll<HTMLButtonElement>("button[data-action]")
        .forEach((btn) => {
          btn.addEventListener("click", async () => {
            const action = btn.dataset.action;
            const userId = Number(btn.dataset.userId);
            const user = this.loadedAccounts.find((a) => a.id === userId);
            if (!user) return;

            if (action === "validate-user") {
              try {
                btn.disabled = true;
                const res = await AdminApi.validateUser(userId);
                ToastComponent.show(res.message, "success");
                await this.loadAccounts();
                await this.loadStats();
              } catch (err: any) {
                ToastComponent.show(err.message || "Erreur lors de la validation.", "error");
                btn.disabled = false;
              }
            } else if (action === "reject-user") {
              if (confirm(`Voulez-vous vraiment refuser l'inscription de "${user.raison_sociale}" ?`)) {
                try {
                  btn.disabled = true;
                  const res = await AdminApi.rejectUser(userId);
                  ToastComponent.show(res.message, "info");
                  await this.loadAccounts();
                  await this.loadStats();
                } catch (err: any) {
                  ToastComponent.show(err.message || "Erreur lors du refus.", "error");
                  btn.disabled = false;
                }
              }
            } else if (action === "edit-user") {
              ModalComponent.openUserAccountModal(user);
            } else if (action === "reset-pwd") {
              ModalComponent.openResetPasswordModal(user);
            } else if (action === "delete-user") {
              ModalComponent.openDeleteUserModal(
                user,
                this.currentUser ? this.currentUser.id === user.id : false,
              );
            }
          });
        });
    } catch (err: any) {
      console.error("Failed to load accounts directory:", err);
      ToastComponent.show("Erreur lors du chargement des comptes.", "error");
    }
  }

  // Legacy alias for compatibility
  public async loadApporteurs(): Promise<void> {
    return this.loadAccounts();
  }

  private async handleSaveUserAccount(e: Event): Promise<void> {
    e.preventDefault();
    const editIdEl = document.getElementById(
      "userAccountEditId",
    ) as HTMLInputElement | null;
    const editId = editIdEl?.value ? Number(editIdEl.value) : null;

    const siret = (
      (document.getElementById("modalUserSiret") as HTMLInputElement)?.value ||
      ""
    ).replace(/\D/g, "");
    const raison_sociale = (
      (document.getElementById("modalUserRaisonSociale") as HTMLInputElement)
        ?.value || ""
    ).trim();
    const email = (
      (document.getElementById("modalUserEmail") as HTMLInputElement)?.value ||
      ""
    ).trim();
    const telephone = (
      (document.getElementById("modalUserTelephone") as HTMLInputElement)
        ?.value || ""
    ).trim();
    const role = ((
      document.getElementById("modalUserRole") as HTMLSelectElement
    )?.value || "apporteur") as any;
    const status = ((
      document.getElementById("modalUserStatus") as HTMLSelectElement
    )?.value || "active") as any;
    const password =
      (document.getElementById("modalUserPassword") as HTMLInputElement)
        ?.value || "";

    if (!siret || siret.length !== 14) {
      ToastComponent.show(
        "Le SIRET doit contenir exactement 14 chiffres.",
        "warning",
      );
      return;
    }
    if (!raison_sociale || raison_sociale.length < 2) {
      ToastComponent.show(
        "La raison sociale doit comporter au moins 2 caractères.",
        "warning",
      );
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      ToastComponent.show("Adresse email invalide.", "warning");
      return;
    }
    if (!telephone || telephone.length < 8) {
      ToastComponent.show("Numéro de téléphone requis.", "warning");
      return;
    }

    const saveBtn = document.getElementById(
      "btnSaveUserAccount",
    ) as HTMLButtonElement | null;
    if (saveBtn) saveBtn.classList.add("loading");

    try {
      if (editId) {
        // Edit existing account
        await AdminApi.updateUser(editId, {
          siret,
          raison_sociale,
          email,
          telephone,
          role,
          status,
        });
        ToastComponent.show(
          `Le compte "${raison_sociale}" a été mis à jour avec succès.`,
          "success",
        );
      } else {
        // Create new account
        const pwdCheck = evaluatePasswordRules(password);
        if (pwdCheck.score < 4) {
          ToastComponent.show(
            "Le mot de passe initial ne respecte pas tous les critères de sécurité.",
            "warning",
          );
          if (saveBtn) saveBtn.classList.remove("loading");
          return;
        }

        await AdminApi.createUser({
          siret,
          raison_sociale,
          email,
          telephone,
          password,
          role,
          status,
        });
        ToastComponent.show(
          `Nouveau compte "${raison_sociale}" créé avec succès.`,
          "success",
        );
      }

      ModalComponent.closeUserAccountModal();
      await Promise.all([
        this.loadAccounts(),
        this.loadApporteursDropdown(),
        this.loadStats(),
      ]);
    } catch (err: any) {
      ToastComponent.show(
        err.message || "Erreur lors de l'enregistrement du compte.",
        "error",
      );
    } finally {
      if (saveBtn) saveBtn.classList.remove("loading");
    }
  }

  private async handleConfirmResetPassword(e: Event): Promise<void> {
    e.preventDefault();
    const idInput = document.getElementById(
      "resetPasswordUserId",
    ) as HTMLInputElement | null;
    const pwdInput = document.getElementById(
      "resetNewPassword",
    ) as HTMLInputElement | null;

    const userId = idInput ? Number(idInput.value) : 0;
    const newPassword = pwdInput ? pwdInput.value : "";

    if (!userId) {
      ToastComponent.show("Identifiant de compte manquant.", "error");
      return;
    }

    const pwdCheck = evaluatePasswordRules(newPassword);
    if (pwdCheck.score < 4) {
      ToastComponent.show(
        "Le mot de passe doit respecter l'ensemble des 4 règles de sécurité.",
        "warning",
      );
      return;
    }

    const confirmBtn = document.getElementById(
      "btnConfirmResetPassword",
    ) as HTMLButtonElement | null;
    if (confirmBtn) confirmBtn.classList.add("loading");

    try {
      await AdminApi.resetPassword(userId, newPassword);
      ToastComponent.show("Mot de passe réinitialisé avec succès.", "success");
      ModalComponent.closeResetPasswordModal();
    } catch (err: any) {
      ToastComponent.show(
        err.message || "Erreur lors de la réinitialisation du mot de passe.",
        "error",
      );
    } finally {
      if (confirmBtn) confirmBtn.classList.remove("loading");
    }
  }

  private async handleConfirmDeleteUser(): Promise<void> {
    const idInput = document.getElementById(
      "deleteTargetUserId",
    ) as HTMLInputElement | null;
    const userId = idInput ? Number(idInput.value) : 0;

    if (!userId) return;

    const confirmBtn = document.getElementById(
      "btnConfirmDeleteUser",
    ) as HTMLButtonElement | null;
    if (confirmBtn) confirmBtn.classList.add("loading");

    try {
      await AdminApi.deleteUser(userId);
      ToastComponent.show("Compte supprimé avec succès.", "success");
      ModalComponent.closeDeleteUserModal();

      await Promise.all([
        this.loadAccounts(),
        this.loadApporteursDropdown(),
        this.loadProspects(),
        this.loadSharedSirets(),
        this.loadStats(),
      ]);
    } catch (err: any) {
      ToastComponent.show(
        err.message || "Erreur lors de la suppression du compte.",
        "error",
      );
    } finally {
      if (confirmBtn) confirmBtn.classList.remove("loading");
    }
  }

}
